import { useTranslation } from "react-i18next";
import {
  Dialog,
  useSnackbarContext,
} from "@inductiveautomation/ws-components";

import {
  MakerLicenseSummaryItem,
  useDeleteMakerLicenseMutation,
} from "../api/makerLicenses.api";
import { addDashToKey } from "../License/utilities";

const DeleteDialog = ({
  open,
  licenses,
  onClose,
  onSubmit,
}: {
  open: boolean;
  licenses: MakerLicenseSummaryItem[];
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const [deleteLicense] = useDeleteMakerLicenseMutation();

  const { t } = useTranslation();
  const { addSnackbar } = useSnackbarContext();
  const testId = "maker-licenses-delete";

  if (licenses.length === 0) return null;

  const deleteLicenses = () => {
    void Promise.all(
      licenses.map(({ key: licenseKey }) => deleteLicense({ licenseKey }))
    )
      .then((responses) => {
        const errors = responses.filter(({ error }) => error);
        if (errors.length) {
          addSnackbar(
            t("maker_licenses.actions.delete.error_message", {
              count: errors.length,
            }),
            { type: "error" }
          );
          onClose();
          return;
        }
        addSnackbar(
          t("maker_licenses.actions.delete.success_message", {
            count: licenses.length,
          })
        );
        onSubmit();
      })
      .catch(() => {
        addSnackbar(
          t("maker_licenses.actions.delete.error_message", {
            count: licenses.length,
          }),
          { type: "error" }
        );
        onClose();
        return;
      });
  };

  return (
    <Dialog
      testId={testId}
      isOpen={open}
      onClose={onClose}
      showCloseButton
      headline={t("maker_licenses.actions.delete.heading", {
        count: licenses.length,
      })}
      supportingText={t("maker_licenses.actions.delete.supporting_text", {
        licenseNumber: addDashToKey(licenses[0].key),
        count: licenses.length,
      })}
      alert={{
        id: `${testId}-alert`,
        description: t("maker_licenses.actions.delete.alert", {
          count: licenses.length,
        }),
        variant: "warning",
        closeButtonEventHandler: () => undefined,
      }}
      actions={[
        {
          testId: `${testId}-cancel`,
          type: "cancel",
          label: t("common.cancel"),
        },
        {
          testId: `${testId}-submit`,
          type: "submit",
          label: t("maker_licenses.actions.delete.buttons.submit", {
            count: licenses.length,
          }),
          onClick: deleteLicenses,
        },
      ]}
    />
  );
};

export default DeleteDialog;
